import { Clock, MapPinOff } from 'lucide-react';
import { StatusBadge } from '@/components/common/StatusBadge';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { STATUS_COLOR_CLASS } from '@/styles/statusColors';
import { formatTime } from '@/utils/date';
import {
  DCR_APPROVAL_STATUS_LABELS,
  DCR_APPROVAL_STATUS_TONE,
  UNLOCATED_STOP_COLOR,
} from '../constants/moTravel.constants';
import type { MoTravelStop } from '../types/moTravel.types';
import { formatDuration } from '../utils/formatDuration';
import type { JourneyEntry } from '../utils/journeyEntries';
import type { StopDetail } from '../utils/routeFocus';
import { MoEntryCard, MoEntryDetailLine } from './MoEntryCard';
import { MoStartCard } from './MoStartCard';
import { MoStopCard } from './MoStopCard';
import { MoTransportBadge } from './MoTransportBadge';

interface EntryCardProps {
  entry: JourneyEntry;
  onClose: () => void;
  hasTintedBody?: boolean | undefined;
  className?: string | undefined;
}

interface UnlocatedStopCardProps extends EntryCardProps {
  stop: MoTravelStop;
}

function UnlocatedStopCard({
  entry,
  stop,
  onClose,
  hasTintedBody,
  className,
}: UnlocatedStopCardProps) {
  return (
    <MoEntryCard
      headerColor={UNLOCATED_STOP_COLOR}
      headerMarker={entry.label}
      heading={`Stop ${entry.label}`}
      date={entry.day.date}
      closeLabel="Close stop details"
      onClose={onClose}
      hasTintedBody={hasTintedBody}
      className={className}
    >
      <div className="flex items-start gap-2">
        <div className="flex min-w-0 flex-1 flex-col gap-1">
          <span className="text-xs-plus leading-snug font-semibold max-lg:text-sm">
            {entry.title}
          </span>
          <span
            className={cn(
              'inline-flex w-fit items-center gap-1 rounded-full px-1.5 py-px text-2xs font-medium',
              STATUS_COLOR_CLASS.danger,
            )}
          >
            <MapPinOff className="size-3" aria-hidden />
            No GPS, so it is not on the map
          </span>
        </div>
        {stop.transportType && (
          <MoTransportBadge
            transportType={stop.transportType}
            tintColor={UNLOCATED_STOP_COLOR}
            size="large"
          />
        )}
      </div>
      <MoEntryDetailLine icon={Clock}>
        <span className="tabular-nums">
          {formatTime(stop.timeFrom)} – {formatTime(stop.timeTo)} ·{' '}
          {formatDuration(stop.spendMinutes)}
        </span>
      </MoEntryDetailLine>
      <div className="flex items-center gap-1.5 border-t pt-2">
        {stop.dcrTypeCode && <Badge variant="outline">{stop.dcrTypeCode}</Badge>}
        <StatusBadge tone={DCR_APPROVAL_STATUS_TONE[stop.approvalStatus]}>
          {DCR_APPROVAL_STATUS_LABELS[stop.approvalStatus]}
        </StatusBadge>
      </div>
    </MoEntryCard>
  );
}

interface MoJourneyEntryCardProps extends EntryCardProps {
  stopDetail: StopDetail | null;
}

export function MoJourneyEntryCard({
  entry,
  stopDetail,
  onClose,
  hasTintedBody,
  className,
}: MoJourneyEntryCardProps) {
  if (stopDetail) {
    return (
      <MoStopCard
        stopDetail={stopDetail}
        onClose={onClose}
        hasTintedBody={hasTintedBody}
        className={className}
      />
    );
  }
  if (entry.kind === 'start') {
    return (
      <MoStartCard
        day={entry.day}
        headerColor={entry.dayColor}
        onClose={onClose}
        hasTintedBody={hasTintedBody}
        className={className}
      />
    );
  }
  if (entry.stop) {
    return (
      <UnlocatedStopCard
        entry={entry}
        stop={entry.stop}
        onClose={onClose}
        hasTintedBody={hasTintedBody}
        className={className}
      />
    );
  }
  return null;
}
